import { useContext, useEffect, useState } from 'react'

import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Paper, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { MultiSectionDialog } from '@scoir/modals'
import { ContentItem, ContentType } from '../../ContentManagementProvider/Context'
import { ExpandMore } from '@material-ui/icons'
import ContentManagementContext from '../../ContentManagementProvider/Context'
import { useGuarenteedScid } from 'common/use-scid'
import { SortableList } from 'components/SortableList'
import { ReorderListItem } from './ReorderItem'
import ModalError from '../ModalError'

interface ContentReorderDetails {
  title: string
  description?: string
  type: string
  isSortable: boolean
}

type ContentData = {
  [K in ContentType]: ContentReorderDetails
}

type ContentItemMap = {
  [K in ContentType]: ContentItem[]
}

const contentMap: ContentData = {
  CollegeOverview: {
    title: 'Overview',
    description: 'The first post will be featured in Discovery. ',
    type: 'Posts',
    isSortable: true,
  },
  Academics: {
    title: 'Academics',
    type: 'Posts',
    isSortable: true,
  },
  Admissions: {
    title: 'Admissions',
    type: 'Posts',
    isSortable: true,
  },
  CostAndFinancialAid: {
    title: 'Cost & Aid',
    type: 'Posts',
    isSortable: true,
  },
  StudentLife: {
    title: 'Student Life',
    type: 'Posts',
    isSortable: true,
  },
  Sessions: {
    title: 'Events',
    description: 'Events will be sorted by date.',
    type: 'Events',
    isSortable: false,
  },
  Programs: {
    title: 'Programs',
    type: 'Programs',
    description: 'Programs will be sorted by last updated date.',
    isSortable: false,
  },
}

const useStyles = makeStyles((theme) => ({
  root: {
    boxShadow: 'none',
    padding: theme.spacing(1),
    // Override mui styling for disabled accordions
    backgroundColor: `${theme.palette.common.white} !important`,
  },
}))

export interface ContentReorderModalProps {
  isOpen: boolean
  onClose: () => void
}
const ContentReorderModal = ({ isOpen, onClose }: ContentReorderModalProps) => {
  const classes = useStyles()
  const scid = useGuarenteedScid()
  const { reorderContent, fetchReorderContent, fetchReorderContentLoading, saveNewOrder, saveNewOrderError } =
    useContext(ContentManagementContext)
  const [map, setMap] = useState<ContentItemMap | undefined>()

  useEffect(() => {
    if (isOpen) {
      fetchReorderContent()
    }
  }, [fetchReorderContent, isOpen])

  useEffect(() => {
    if (!fetchReorderContentLoading) {
      const contentMap: ContentItemMap = {
        CollegeOverview: [],
        Academics: [],
        Admissions: [],
        CostAndFinancialAid: [],
        StudentLife: [],
        Sessions: [],
        Programs: [],
      }
      reorderContent?.items
        ?.sort((a, b) => a.sortIndex - b.sortIndex)
        .forEach((s) => {
          if (contentMap[s.contentType]) {
            contentMap[s.contentType].push(s)
            return
          }
          contentMap[s.contentType] = [s]
        })
      setMap(contentMap)
    }
  }, [reorderContent, fetchReorderContentLoading])

  const [accordionStates, setAccordionStates] = useState<{ [key: number]: boolean }>({})
  const toggleAccordion = (idx: number) => {
    setAccordionStates((prevState) => ({
      ...prevState,
      [idx]: !prevState[idx],
    }))
  }
  const toggleAllAccordions = (open: boolean) => {
    if (map) {
      const updatedStates = Object.keys(map).reduce((acc: any, _, idx) => {
        acc[idx] = open
        return acc
      }, {})
      setAccordionStates(updatedStates)
    }
  }
  const handleOnClose = () => {
    onClose()
  }
  const allClosed = Object.values(accordionStates).every((state) => !state)

  if (!map) return null
  return (
    <MultiSectionDialog
      open={isOpen}
      onClose={onClose}
      scroll="paper"
      headerText="Manage Content Order"
    >
      <MultiSectionDialog.Body>
        <Box
          display="flex"
          flexDirection="column"
          gridGap={16}
        >
          <Typography>Drag and drop to reorder live content on your profile</Typography>
          <Box
            display="flex"
            flexDirection="column"
            gridGap={16}
          >
            {Object.keys(map).map((type, idx) => {
              const t = type as ContentType
              const items = map[t]
              const liveItems = items.filter((i) => !i.isHidden)

              const sectionDetails = contentMap[t]

              return (
                <Paper
                  elevation={0}
                  key={`${type}-${idx}`}
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    width="100%"
                    gridGap={8}
                  >
                    <Accordion
                      disabled={liveItems.length === 0}
                      expanded={(accordionStates[idx] && liveItems.length > 0) ?? false}
                      onChange={() => toggleAccordion(idx)}
                      classes={{
                        root: classes.root,
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMore color={liveItems.length === 0 ? 'disabled' : 'inherit'} />}
                        aria-label={`Expand ${sectionDetails.title}`}
                        aria-controls={`${type + idx}-accordion-content`}
                        id={`${type + idx}-accordion-header`}
                      >
                        <Box
                          display="flex"
                          flexDirection="column"
                        >
                          <Typography
                            variant="h6"
                            color="textPrimary"
                          >
                            {sectionDetails.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                          >
                            {sectionDetails.description
                              ? sectionDetails.description
                              : `${liveItems.length} ${sectionDetails.type}`}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <SortableList
                          items={liveItems}
                          onChange={(items) => {
                            const oldMap = map
                            const updatedItems = items.map((item, index) => {
                              if (index === 0 && item.contentType === 'CollegeOverview') {
                                return { ...item, isFeatured: true }
                              } else {
                                return { ...item, isFeatured: false }
                              }
                            })
                            setMap({
                              ...map,
                              [t]: updatedItems,
                            })
                            const newOrder = items.map((s) => s.id)
                            saveNewOrder({
                              scid,
                              type: t,
                              newOrder,
                              onError: () => {
                                setMap(oldMap)
                              },
                            })
                          }}
                          renderItem={(item) => (
                            <SortableList.Item id={item.id}>
                              {sectionDetails.isSortable ? (
                                <SortableList.DragHandle>
                                  <ReorderListItem contentItem={item} />
                                </SortableList.DragHandle>
                              ) : (
                                <ReorderListItem
                                  contentItem={item}
                                  isSortable={sectionDetails.isSortable}
                                />
                              )}
                            </SortableList.Item>
                          )}
                        />
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                </Paper>
              )
            })}
          </Box>
        </Box>
      </MultiSectionDialog.Body>
      <MultiSectionDialog.Footer>
        <Box
          width="100%"
          display="flex"
          flexDirection="column"
        >
          {saveNewOrderError && <ModalError />}
          <Box
            display="flex"
            gridGap={8}
            alignItems="center"
            justifyContent="flex-end"
          >
            <Button
              variant="outlined"
              color="primary"
              onClick={() => {
                toggleAllAccordions(allClosed)
              }}
            >
              {allClosed ? 'Expand All' : 'Close All'}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleOnClose}
            >
              Done
            </Button>
          </Box>
        </Box>
      </MultiSectionDialog.Footer>
    </MultiSectionDialog>
  )
}

export default ContentReorderModal
